import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Globe, Shield, Users, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import StrategyWorkspaceHeader from './StrategyWorkspaceHeader';
import { competitorsApi } from '../../services/api';
import { useQuestions, QuestionResponse } from '../../hooks/useQuestions';
import { useExternalAssessmentProgress } from '../../hooks/useExternalAssessmentProgress';
import {
  mergeManualPositioningEvidence,
  type CompetitorAIInsights,
} from '../../utils/competitorResearch';

// Import section components directly to allow immediate progress calculation
import PestelSection from './external-assessment/PestelSection';
import FiveForcesSection from './external-assessment/FiveForcesSection';
import CompetitorsSection from './external-assessment/CompetitorsSection';
import DrivingForcesSection from './external-assessment/DrivingForcesSection';

interface ExternalAssessmentData {
  pestel: {
    [key: string]: {
      [factor: string]: 'positive' | 'negative' | null;
    };
  };
  fiveForces: {
    [force: string]: string[];
  };
  competitors: Array<{
    id: string;
    name: string;
    description?: string;
    website?: string;
    priceQuality: 'low' | 'medium' | 'high' | 'unknown';
    geoScope: 'local' | 'regional' | 'national' | 'international' | 'unknown';
    marketSize: number | null;
    aiInsights?: CompetitorAIInsights | null;
    strategy?: string;
    capabilities?: string;
    objectives?: string;
    assumptions?: string;
    revenue?: number;
    employees?: number;
    foundedYear?: number;
    priority?: 'low' | 'medium' | 'high';
    lastScrapedAt?: string;
    investments?: string;
    marketPosition?: string;
    capabilityAnalysis?: string;
  }>;
  drivingForces: {
    [key: string]: string[];
  };
  analysisQuestions: {
    demandTrend: string;
    competitionIntensity: string;
    profitabilityImpact: string;
  };
}

export default function ExternalAssessmentPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const sectionHash = location.hash.slice(1);
  const activeSection = ['pestel', 'fiveForces', 'competitors', 'drivingForces'].includes(
    sectionHash
  )
    ? sectionHash
    : 'pestel';

  // Use external assessment progress hook for consistent progress tracking
  const { progress: externalProgress } = useExternalAssessmentProgress(companyId || '');

  // Use questions hook for analysis questions (no entityKey filter to load all responses)
  const analysisQuestionsHook = useQuestions('industry_driving.summary', companyId || '');

  const handleSectionChange = useCallback(
    (section: string) => {
      navigate(
        { pathname: location.pathname, search: location.search, hash: `#${section}` },
        { replace: true, preventScrollReset: true, state: location.state }
      );
    },
    [navigate, location.pathname, location.search, location.state]
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousScrollPosition = useRef<number>(0);
  const competitorScope = useRef({
    companyId,
    fetchId: 0,
    writes: new Map<string, { version: number; fields: Map<string, number> }>(),
  });
  if (competitorScope.current.companyId !== companyId) {
    competitorScope.current = { companyId, fetchId: 0, writes: new Map() };
  }
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [externalData, setExternalData] = useState<ExternalAssessmentData>({
    pestel: {},
    fiveForces: {},
    competitors: [],
    drivingForces: {},
    analysisQuestions: {
      demandTrend: '',
      competitionIntensity: '',
      profitabilityImpact: '',
    },
  });

  // Progress calculation functions - now handled by the external assessment progress hook

  const pestelProgress = externalProgress.pestel;
  const fiveForcesProgress = externalProgress.fiveForces;
  const competitorsProgress = externalProgress.competitors;
  const drivingForcesProgress = externalProgress.drivingForces;
  const overallProgress = externalProgress.overall;

  // Auto-save functionality removed - now saving directly to database on each change

  // Helper function to preserve scroll position
  const preserveScrollPosition = () => {
    if (scrollContainerRef.current) {
      previousScrollPosition.current = scrollContainerRef.current.scrollTop;
    }
  };

  // Helper functions for user interaction - simplified since we no longer track typing for auto-save
  const handleInputFocus = useCallback(() => {
    // No longer need to track typing
  }, []);

  const handleInputBlur = useCallback(() => {
    // No longer need to track typing
  }, []);

  const handleInputChange = useCallback(() => {
    // No longer need to track typing
  }, []);

  // Restore scroll position only when activeSection changes
  useEffect(() => {
    if (scrollContainerRef.current && previousScrollPosition.current > 0) {
      scrollContainerRef.current.scrollTop = previousScrollPosition.current;
      previousScrollPosition.current = 0; // Reset after restoration
    }
  }, [activeSection]);

  useEffect(() => {
    const scope = competitorScope.current;
    const fetchId = ++scope.fetchId;
    let active = true;
    const fetchCompany = async () => {
      if (!companyId) return;

      try {
        setIsLoading(true);
        setExternalData((previous) => ({ ...previous, competitors: [] }));
        // Load external data
        try {
          // Load competitors from database
          const competitorsResponse = await competitorsApi.getCompetitors(companyId);
          if (!active || competitorScope.current !== scope || scope.fetchId !== fetchId) return;
          if (competitorsResponse.success && competitorsResponse.data) {
            setExternalData((prev) => ({
              ...prev,
              competitors: competitorsResponse.data || [],
            }));
          }

          // Load analysis questions from database instead of localStorage
          // The analysis questions will be loaded through the useQuestions hook

          // TODO: Load other external assessment data (PESTEL, Five Forces, etc.)
          // const data = await api.loadExternalAssessment(companyId);
          // if (data) {
          //   setExternalData(data);
          // }
        } catch (error) {
          console.error('Failed to load external assessment:', error);
        }
      } catch (error) {
        console.error('Failed to fetch company:', error);
      } finally {
        if (active && competitorScope.current === scope) setIsLoading(false);
      }
    };

    fetchCompany();
    return () => {
      active = false;
    };
  }, [companyId]);

  // Load analysis questions from database when questions hook is ready
  useEffect(() => {
    if (!analysisQuestionsHook.loading && analysisQuestionsHook.questions.length > 0 && companyId) {
      const analysisQuestions = {
        demandTrend: '',
        competitionIntensity: '',
        profitabilityImpact: '',
      };

      // Load saved responses for each analysis question
      ['demandTrend', 'competitionIntensity', 'profitabilityImpact'].forEach((questionKey) => {
        // Find the actual question for this analysis type
        let question;
        if (questionKey === 'demandTrend') {
          question = analysisQuestionsHook.questions.find(
            (q) =>
              q.content &&
              q.content.toLowerCase().includes('demand') &&
              q.content.toLowerCase().includes('increase or decrease')
          );
        } else if (questionKey === 'competitionIntensity') {
          question = analysisQuestionsHook.questions.find(
            (q) =>
              q.content &&
              q.content.toLowerCase().includes('competition') &&
              q.content.toLowerCase().includes('more or less intense')
          );
        } else if (questionKey === 'profitabilityImpact') {
          question = analysisQuestionsHook.questions.find(
            (q) =>
              q.content &&
              q.content.toLowerCase().includes('profitability') &&
              q.content.toLowerCase().includes('higher or lower')
          );
        }

        if (question) {
          // Check if this question has responses with the correct entityKey
          // Since we're not filtering by entityKey in the API call, we need to filter here
          const questionResponse = question.responses?.find(
            (r) =>
              r.id &&
              r.response &&
              r.response.selectedOptions &&
              r.response.selectedOptions.length > 0
          );

          if (questionResponse) {
            const dbValue = questionResponse.response.selectedOptions![0];

            // Map database values back to frontend values
            let frontendValue = dbValue;
            if (questionKey === 'demandTrend') {
              if (dbValue === 'Increase') frontendValue = 'increasing';
              else if (dbValue === 'Decrease') frontendValue = 'decreasing';
              else if (dbValue === 'Neutral') frontendValue = 'neutral';
            } else if (questionKey === 'competitionIntensity') {
              if (dbValue === 'More') frontendValue = 'more_intense';
              else if (dbValue === 'Less') frontendValue = 'less_intense';
              else if (dbValue === 'No Change') frontendValue = 'no_change';
            } else if (questionKey === 'profitabilityImpact') {
              if (dbValue === 'Higher') frontendValue = 'higher';
              else if (dbValue === 'Lower') frontendValue = 'lower';
              else if (dbValue === 'Unchanged') frontendValue = 'unchanged';
            }

            analysisQuestions[questionKey as keyof typeof analysisQuestions] = frontendValue;
          }
        }
      });

      setExternalData((prev) =>
        prev.analysisQuestions.demandTrend === analysisQuestions.demandTrend &&
        prev.analysisQuestions.competitionIntensity === analysisQuestions.competitionIntensity &&
        prev.analysisQuestions.profitabilityImpact === analysisQuestions.profitabilityImpact
          ? prev
          : { ...prev, analysisQuestions }
      );
    }
  }, [analysisQuestionsHook.loading, analysisQuestionsHook.questions, companyId]);

  // PESTEL and Five Forces handlers removed - now handled by individual sections

  const addCompetitor = async () => {
    if (!companyId) return;

    try {
      // Generate a unique name for the new competitor
      const generateUniqueName = () => {
        const baseName = 'New Competitor';
        const existingNames = externalData.competitors.map((c) => c.name);

        if (!existingNames.includes(baseName)) {
          return baseName;
        }

        let counter = 2;
        while (existingNames.includes(`${baseName} ${counter}`)) {
          counter++;
        }
        return `${baseName} ${counter}`;
      };

      const newCompetitorData = {
        name: generateUniqueName(),
        priceQuality: 'medium' as const,
        geoScope: 'local' as const,
        marketSize: 0,
      };

      const response = await competitorsApi.createCompetitor(companyId, newCompetitorData);
      if (response.success && response.data) {
        setExternalData((prev) => ({
          ...prev,
          competitors: [...prev.competitors, response.data],
        }));
      }
    } catch (error) {
      console.error('Failed to add competitor:', error);
    }
  };

  const updateCompetitor = async (id: string, field: string, value: any) => {
    if (!companyId) return;
    const scope = competitorScope.current;
    const write = scope.writes.get(id) || { version: 0, fields: new Map<string, number>() };
    scope.writes.set(id, write);
    const requestVersion = ++write.version;
    write.fields.set(field, requestVersion);
    preserveScrollPosition();

    // Update local state immediately for responsive UI
    setExternalData((prev) => ({
      ...prev,
      competitors: prev.competitors.map((competitor) =>
        competitor.id === id ? { ...competitor, [field]: value } : competitor
      ),
    }));

    try {
      const response = await competitorsApi.updateCompetitor(id, { [field]: value });
      if (
        !mounted.current ||
        competitorScope.current !== scope ||
        !response.success ||
        !response.data ||
        response.data.id !== id ||
        (response.data.companyId && response.data.companyId !== companyId)
      )
        return;
      const latest = write.version === requestVersion;
      setExternalData((previous) => {
        if (competitorScope.current !== scope) return previous;
        return {
          ...previous,
          competitors: previous.competitors.map((competitor) => {
            if (competitor.id !== id) return competitor;
            const updated = latest ? { ...competitor, ...response.data } : { ...competitor };
            // Concurrent edits to other fields remain visible while their saves finish.
            for (const [editedField, version] of write.fields) {
              if (editedField !== field || version !== requestVersion)
                updated[editedField] = (competitor as any)[editedField];
            }
            if (!latest && write.fields.get(field) === requestVersion)
              updated[field] = response.data[field];
            updated.aiInsights = mergeManualPositioningEvidence(
              competitor.aiInsights,
              response.data.aiInsights,
              !latest
            );
            return updated;
          }),
        };
      });
    } catch (error) {
      console.error('Failed to update competitor:', error);
      // Could implement rollback here if needed
    }
  };

  const removeCompetitor = async (id: string) => {
    try {
      await competitorsApi.deleteCompetitor(id);
      setExternalData((prev) => ({
        ...prev,
        competitors: prev.competitors.filter((competitor) => competitor.id !== id),
      }));
    } catch (error) {
      console.error('Failed to remove competitor:', error);
    }
  };

  const refetchCompetitors = async () => {
    if (!companyId) return;
    const scope = competitorScope.current;
    const fetchId = ++scope.fetchId;

    try {
      const competitorsResponse = await competitorsApi.getCompetitors(companyId);
      if (!mounted.current || competitorScope.current !== scope || scope.fetchId !== fetchId)
        return;
      if (competitorsResponse.success && competitorsResponse.data) {
        setExternalData((prev) => ({
          ...prev,
          competitors: competitorsResponse.data || [],
        }));
      }
    } catch (error) {
      console.error('Failed to refetch competitors:', error);
    }
  };

  // Driving forces handler removed - now handled by DrivingForcesSection

  const updateAnalysisQuestion = useCallback(
    async (question: keyof ExternalAssessmentData['analysisQuestions'], value: string) => {
      preserveScrollPosition();

      // Update local state immediately for responsive UI
      setExternalData((prev) => ({
        ...prev,
        analysisQuestions: {
          ...prev.analysisQuestions,
          [question]: value,
        },
      }));

      // Save to database using the questions hook
      if (companyId && analysisQuestionsHook.saveResponse) {
        try {
          // Find the question for this analysis question type based on content keywords
          let analysisQuestion;
          if (question === 'demandTrend') {
            analysisQuestion = analysisQuestionsHook.questions.find(
              (q) =>
                q.content &&
                q.content.toLowerCase().includes('demand') &&
                q.content.toLowerCase().includes('increase or decrease')
            );
          } else if (question === 'competitionIntensity') {
            analysisQuestion = analysisQuestionsHook.questions.find(
              (q) =>
                q.content &&
                q.content.toLowerCase().includes('competition') &&
                q.content.toLowerCase().includes('more or less intense')
            );
          } else if (question === 'profitabilityImpact') {
            analysisQuestion = analysisQuestionsHook.questions.find(
              (q) =>
                q.content &&
                q.content.toLowerCase().includes('profitability') &&
                q.content.toLowerCase().includes('higher or lower')
            );
          }

          if (analysisQuestion) {
            // Map frontend values to database option values
            let dbValue = value;
            if (question === 'demandTrend') {
              if (value === 'increasing') dbValue = 'Increase';
              else if (value === 'decreasing') dbValue = 'Decrease';
              else if (value === 'neutral') dbValue = 'Neutral';
            } else if (question === 'competitionIntensity') {
              if (value === 'more_intense') dbValue = 'More';
              else if (value === 'less_intense') dbValue = 'Less';
              else if (value === 'no_change') dbValue = 'No Change';
            } else if (question === 'profitabilityImpact') {
              if (value === 'higher') dbValue = 'Higher';
              else if (value === 'lower') dbValue = 'Lower';
              else if (value === 'unchanged') dbValue = 'Unchanged';
            }

            const response: QuestionResponse = {
              questionId: analysisQuestion.id,
              entityType: 'industry_driving.summary',
              entityKey: question, // Use the question name as entity key
              response: { selectedOptions: [dbValue] },
            };

            const success = await analysisQuestionsHook.saveResponse(response);
            if (!success) {
              console.error('Failed to save analysis question to database');
            }
          } else {
            console.error('Could not find analysis question in database:', question);
          }
        } catch (error) {
          console.error('Error saving analysis question:', error);
        }
      }
    },
    [companyId, analysisQuestionsHook.questions, analysisQuestionsHook.saveResponse]
  );

  // Progress change handlers are no longer needed - using external assessment progress hook

  if (isLoading) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' as any }}
          className="h-8 w-8 border-2 border-green-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="company-workspace-screen strategy-editor-page">
      <main>
        <StrategyWorkspaceHeader
          companyId={companyId || ''}
          step={2}
          stage="External assessment"
          title="See your market clearly."
          description="Map your market, competitive pressure, and the changes that shape your next move."
          progress={overallProgress}
          activeSection={activeSection}
          sections={[
            { id: 'pestel', label: 'PESTEL', icon: Globe, progress: pestelProgress },
            { id: 'fiveForces', label: 'Five Forces', icon: Shield, progress: fiveForcesProgress },
            { id: 'competitors', label: 'Competitors', icon: Users, progress: competitorsProgress },
            {
              id: 'drivingForces',
              label: 'Driving forces',
              icon: TrendingUp,
              progress: drivingForcesProgress,
            },
          ]}
          onSectionChange={handleSectionChange}
          next={{
            label: 'Assess your strengths',
            href: `/company/${companyId}/strategy/internal-assessment`,
          }}
        />

        {/* Content Area - Scrollable */}
        <div ref={scrollContainerRef} className="strategy-editor-content">
          <div className="w-full h-full">
            {/* Render only the active section for better performance */}
            {activeSection === 'pestel' && <PestelSection companyId={companyId!} />}

            {activeSection === 'fiveForces' && <FiveForcesSection companyId={companyId!} />}

            {activeSection === 'competitors' && (
              <CompetitorsSection
                companyId={companyId!}
                externalData={externalData}
                competitorsProgress={competitorsProgress}
                onAddCompetitor={addCompetitor}
                onUpdateCompetitor={updateCompetitor}
                onRemoveCompetitor={removeCompetitor}
                onRefetchCompetitors={refetchCompetitors}
                onInputFocus={handleInputFocus}
                onInputBlur={handleInputBlur}
                onInputChange={handleInputChange}
              />
            )}

            {activeSection === 'drivingForces' && (
              <DrivingForcesSection
                companyId={companyId!}
                externalData={externalData}
                onUpdateAnalysisQuestion={updateAnalysisQuestion}
                onInputFocus={handleInputFocus}
                onInputBlur={handleInputBlur}
                onInputChange={handleInputChange}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
